// 语音处理工具函数
// 从TalktasticVoiceGame.js提取的语音处理相关逻辑

import { isIPadSafari } from './deviceDetection';
import { createWebAudioRecorder } from './webAudioRecorder';

/**
 * 语音合成 - 使用Web Speech API播放文本
 * @param {string} text 要播放的文本
 * @param {string} langCode 语言代码，默认为'en-US'
 * @param {number} rate 语速，默认为1
 * @param {number} pitch 音调，默认为1
 */
export function speakText(text, langCode = 'en-US', rate = 1, pitch = 1) {
  if (!text || !window.speechSynthesis) return;
  
  // 取消当前正在播放的任何语音
  window.speechSynthesis.cancel();
  
  const utterance = new SpeechSynthesisUtterance(text);
  utterance.lang = langCode;
  utterance.rate = rate;
  utterance.pitch = pitch;
  
  window.speechSynthesis.speak(utterance);
  
  return {
    stop: () => window.speechSynthesis.cancel(),
    pause: () => window.speechSynthesis.pause(),
    resume: () => window.speechSynthesis.resume()
  };
}

/**
 * 开始语音识别
 * Safari → Apple 服务器（国内可用）
 * Chrome/Edge → Google 服务器（需VPN）
 * 不支持的浏览器 → 提示用户换浏览器
 */
export function startSpeechRecognition(langCode = 'en-US', onResult, onEnd, onError) {
  if (isIPadSafari()) {
    console.log("检测到iPad Safari，使用Web Audio API替代方案");
    return startWebAudioRecognition(langCode, onResult, onEnd, onError);
  }

  if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
    console.error("浏览器不支持语音识别API");
    if (onError) onError('not-supported');
    return null;
  }

  return startWebSpeechRecognition(langCode, onResult, onEnd, onError);
}

function startWebSpeechRecognition(langCode, onResult, onEnd, onError) {
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const recognition = new SpeechRecognition();

  recognition.lang = langCode;
  recognition.interimResults = true;
  recognition.continuous = true;
  recognition.maxAlternatives = 1;

  let gotResult = false;
  let hadError = false;
  const startTime = Date.now();

  recognition.onresult = (event) => {
    if (!onResult) return;
    gotResult = true;

    let finalTranscript = '';
    let interimTranscript = '';

    for (let i = 0; i < event.results.length; i++) {
      if (event.results[i].isFinal) {
        finalTranscript += event.results[i][0].transcript + ' ';
      } else {
        interimTranscript += event.results[i][0].transcript;
      }
    }

    const combined = (finalTranscript + interimTranscript).trim();
    const allFinal = interimTranscript.length === 0 && finalTranscript.length > 0;

    onResult([{
      transcript: combined,
      confidence: event.results[event.results.length - 1][0].confidence,
      isFinal: allFinal
    }]);
  };

  recognition.onerror = (event) => {
    if (event.error === 'no-speech') return;
    hadError = true;
    console.error("语音识别错误:", event.error);
    if (onError) onError(event.error);
  };

  recognition.onend = () => {
    const elapsed = Date.now() - startTime;
    console.log(`语音识别结束 (${elapsed}ms, gotResult=${gotResult}, hadError=${hadError})`);

    if (!gotResult && !hadError && elapsed < 3000) {
      console.warn("语音识别静默失败：可能无法连接 Google 服务器");
      if (onError) onError('network');
      return;
    }

    if (onEnd) onEnd();
  };

  try {
    recognition.start();
    console.log("🎤 语音识别已启动 (Web Speech continuous mode)");
  } catch (error) {
    console.error("启动语音识别失败:", error);
    if (onError) onError(error);
    return null;
  }

  return {
    isAzure: false,
    stop: () => { try { recognition.stop(); } catch (e) { /* */ } },
    abort: () => { try { recognition.abort(); } catch (e) { /* */ } },
    recognition
  };
}

// 保存iPad录音器的全局引用，以便在倒计时结束时可以访问
let globalIpadRecorder = null;
let globalIpadFullTranscription = '';

/**
 * 使用Web Audio API进行录音，然后用非连续模式的语音识别处理结果
 * 这是为iPad Safari设备提供的替代方案
 * 
 * @param {string} langCode 语言代码，默认为'en-US'
 * @param {Function} onResult 识别结果回调
 * @param {Function} onEnd 识别结束回调
 * @param {Function} onError 错误回调
 * @returns {Object} 语音识别控制对象
 */
function startWebAudioRecognition(langCode = 'en-US', onResult, onEnd, onError) {
  console.log("启动Web Audio API录音替代方案");
  console.log(`语音识别配置: 语言=${langCode}`);
  
  let isActive = true;
  let fullTranscription = '';
  
  // 重置全局转录
  globalIpadFullTranscription = '';
  
  // 创建音频录制器
  const recorder = createWebAudioRecorder(
    // 数据可用回调 - 这里我们不需要处理中间数据
    null,
    // 完成回调 - 当录音结束时处理音频
    async (audioBlob) => {
      if (!isActive) return;
      
      console.log("录音完成，大小:", audioBlob.size, "字节");
      
      try {
        // 使用非连续模式的语音识别处理录音结果
        await processRecordingWithSpeechRecognition(audioBlob, langCode);
      } catch (error) {
        console.error('处理录音失败:', error);
        if (onError) onError(error);
      }
    }
  );
  
  // 保存到全局引用
  globalIpadRecorder = recorder;
  
  // 开始录音
  recorder.start().catch(error => {
    console.error('开始录音失败:', error);
    if (onError) onError(error);
  });
  
  /**
   * 使用非连续模式的SpeechRecognition API处理录音结果
   * @param {Blob} audioBlob 录音的音频数据
   */
  async function processRecordingWithSpeechRecognition(audioBlob) {
    // 检查浏览器支持
    if (!window.SpeechRecognition && !window.webkitSpeechRecognition) {
      console.error("浏览器不支持语音识别API");
      if (onError) onError(new Error('Speech recognition not supported'));
      return;
    }
    
    console.log("使用非连续模式SpeechRecognition处理录音");
    
    try {
      // 创建语音识别实例 - 使用非连续模式
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      // 配置识别器 - 注意这里设置continuous为false
      recognition.lang = langCode;
      recognition.interimResults = false;
      recognition.continuous = false;
      
      console.log(`语音识别配置: 语言=${langCode}, 中间结果=${recognition.interimResults}, 连续模式=${recognition.continuous}`);
      
      // 创建音频元素并加载录音数据
      const audioURL = URL.createObjectURL(audioBlob);
      const audio = new Audio(audioURL);
      
      // 设置识别结果处理
      recognition.onresult = (event) => {
        if (!isActive) return;
        
        if (event.results && event.results.length > 0) {
          const transcript = event.results[0][0].transcript;
          const confidence = event.results[0][0].confidence;
          
          console.log("识别结果:", transcript, "置信度:", confidence);
          
          // 更新完整转录
          fullTranscription += transcript + ' ';
          globalIpadFullTranscription += transcript + ' ';
          
          // 调用结果回调
          if (onResult) {
            onResult([{
              transcript: fullTranscription,
              confidence: confidence,
              isFinal: true
            }]);
          }
        }
      };
      
      // 设置错误处理
      recognition.onerror = (event) => {
        console.error('语音识别错误:', event.error, event);
        if (onError) onError(event.error);
      };
      
      // 设置结束处理
      recognition.onend = () => {
        console.log("语音识别处理完成");
        URL.revokeObjectURL(audioURL);
      };
      
      // 开始识别
      recognition.start();
      console.log("语音识别已启动");
      
      // 播放音频 (这是必要的，因为SpeechRecognition需要听到音频)
      console.log("开始播放录音");
      await audio.play();
      console.log("录音播放完成");
      
    } catch (error) {
      console.error('处理录音时出错:', error);
      if (onError) onError(error);
    }
  }
  
  // 返回控制对象
  return {
    stop: async () => {
      console.log("停止Web Audio录音");
      isActive = false;
      
      // 停止录音并获取录音数据
      const audioBlob = await recorder.stop();
      
      // 确保在停止录音后，将当前的转录内容传递给回调
      if (onResult && globalIpadFullTranscription) {
        console.log("iPad录音停止时发送最终转录:", globalIpadFullTranscription);
        onResult([{
          transcript: globalIpadFullTranscription,
          confidence: 0.9,
          isFinal: true
        }]);
      }
      
      if (onEnd) {
        console.log("调用录音结束回调");
        onEnd();
      }
      
      return audioBlob;
    },
    abort: () => {
      console.log("中止Web Audio录音");
      isActive = false;
      recorder.stop();
      
      // 确保在中止录音后，将当前的转录内容传递给回调
      if (onResult && globalIpadFullTranscription) {
        console.log("iPad录音中止时发送最终转录:", globalIpadFullTranscription);
        onResult([{
          transcript: globalIpadFullTranscription,
          confidence: 0.9,
          isFinal: true
        }]);
      }
      
      if (onEnd) onEnd();
    }
  };
}

/**
 * 获取支持的语音合成声音
 * @param {string} langCode 语言代码过滤条件，如'en-US'
 * @returns {Array} 可用的声音列表
 */
export function getAvailableVoices(langCode) {
  if (!window.speechSynthesis) return [];
  
  const voices = window.speechSynthesis.getVoices();
  
  if (langCode) {
    return voices.filter(voice => voice.lang.includes(langCode));
  }
  
  return voices;
}

/**
 * 强制停止所有iPad录音并处理结果
 * 在倒计时结束时调用此函数
 * 
 * @param {Function} onResult 识别结果回调
 * @param {Function} onEnd 识别结束回调
 */
export function forceStopIPadRecording(onResult, onEnd) {
  console.log("强制停止iPad录音");
  
  if (globalIpadRecorder) {
    // 停止录音
    globalIpadRecorder.stop().then(() => {
      // 如果有转录内容，发送给回调
      if (globalIpadFullTranscription && onResult) {
        console.log("强制停止时发送最终转录:", globalIpadFullTranscription);
        onResult([{
          transcript: globalIpadFullTranscription,
          confidence: 0.9,
          isFinal: true
        }]);
      }
      
      // 调用结束回调
      if (onEnd) {
        console.log("调用强制结束回调");
        onEnd();
      }
      
      // 清理全局引用
      globalIpadRecorder = null;
      globalIpadFullTranscription = '';
    });
  }
}

export default {
  speakText,
  startSpeechRecognition,
  getAvailableVoices,
  forceStopIPadRecording
};
